import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Switch, message, Popconfirm, Space } from 'antd';
import { carrierApi, type CarrierDTO } from '../api/kfc';
import { clientTablePagination } from '../utils/tablePagination';

const CarrierManage: React.FC = () => {
  const [list, setList] = useState<CarrierDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    carrierApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载承运商列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setModalOpen(true);
  };

  const openEdit = (r: CarrierDTO) => {
    setEditingId(r.id);
    form.setFieldsValue({
      carrierCode: r.carrierCode,
      carrierName: r.carrierName,
      remark: r.remark,
      isActive: r.isActive !== false,
    });
    setModalOpen(true);
  };

  const onFinish = (values: Record<string, unknown>) => {
    const payload: Partial<CarrierDTO> = {
      carrierCode: values.carrierCode as string,
      carrierName: values.carrierName as string,
      remark: values.remark as string | undefined,
      isActive: values.isActive as boolean,
    };
    const submit = editingId ? carrierApi.update(editingId, payload) : carrierApi.create(payload);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const onDelete = (id: string) => {
    carrierApi
      .delete(id)
      .then(() => {
        message.success('已删除');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  return (
    <>
      <Card title="承运商列表" extra={<Button type="primary" onClick={openAdd}>新增承运商</Button>}>
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          pagination={clientTablePagination()}
          columns={[
            { title: '编码', dataIndex: 'carrierCode', width: 140 },
            { title: '名称', dataIndex: 'carrierName', width: 160, ellipsis: true },
            { title: '备注', dataIndex: 'remark', ellipsis: true },
            {
              title: '启用',
              dataIndex: 'isActive',
              width: 80,
              render: (v: boolean) => (v !== false ? '是' : '否'),
            },
            {
              title: '操作',
              width: 140,
              render: (_, r) => (
                <Space>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm title="确定删除该承运商？" onConfirm={() => onDelete(r.id)}>
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑承运商' : '新增承运商'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="carrierCode" label="承运商编码" rules={[{ required: true, message: '请输入编码' }]}>
            <Input disabled={!!editingId} />
          </Form.Item>
          <Form.Item name="carrierName" label="承运商名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input />
          </Form.Item>
          <Form.Item name="isActive" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CarrierManage;
